import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@verantos-dev/app-state-base';
import {
  clearItemChangeStatuses,
  markConceptGroupSelectionsAsReported,
} from '@verantos-dev/app-state-cohort-builder';
import {
  clearCohortProcessPendingMarker,
  COHORT_PROCESS_PENDING_TTL_MS,
  readCohortProcessPendingTimestamp,
  selectIsExecutionPending,
  selectLatestCohortVersionId,
  selectLatestStatus,
  useCohortDefinitionStore,
  writeCohortProcessPendingMarker,
} from '@verantos-dev/app-state-cohort-definition';
import {
  addResearchAssistantMessages,
  COHORT_ACTIONS,
  emitSyncConceptGroupSelections,
  selectResearchAssistantModel,
} from '@verantos-dev/app-state-research-assistant';
import { useAuth } from '@verantos-dev/core-auth';
import { useProcessCohortMutation } from '@verantos-dev/data-access-cohorts';
import { emitUserSaveEvent } from '@verantos-dev/feature-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import { ResearchAssistantMessageEnumLegacy } from '@verantos-dev/types-common';

import { useCohortBuilderSaveQuery } from '../../hooks/common/useCohortBuilderSaveQuery';

// TODO: cleanup with skip_process_confirmation_modal ff
// Fires cohort processing on click and exposes its in-flight/error state for the Process button.
export const useInlineProcessCohort = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const latestCohortVersionId = useCohortDefinitionStore(selectLatestCohortVersionId);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const isExecutionPending = useCohortDefinitionStore(selectIsExecutionPending);
  // Read the flag already hydrated onto the store (useCohortProfileData)
  const isInlineProcessEnabled = useCohortDefinitionStore(
    (s) => s.skipProcessConfirmationModalEnabled
  );
  const researchAssistantModel = useAppSelector(selectResearchAssistantModel);
  const { user } = useAuth();

  const {
    mutateAsync: postCohortExecution,
    isPending: isProcessSubmitting,
    error: processError,
    reset: resetProcessError,
  } = useProcessCohortMutation();

  // eagerTranslation: false prevents a SQL-translation call on every jsonDsl change
  const { projectId, cohortId, submitVersion, isInitialLoading } = useCohortBuilderSaveQuery(
    postCohortExecution,
    { eagerTranslation: isInlineProcessEnabled }
  );

  // TanStack only clears a mutation error on the next `mutate`/`reset()`, so
  // clear it once a poll confirms the run is genuinely underway
  useEffect(() => {
    if (
      processError &&
      !isProcessSubmitting &&
      latestStatus === CohortDefinitionStatus.PROCESSING
    ) {
      resetProcessError();
    }
  }, [processError, isProcessSubmitting, latestStatus, resetProcessError]);

  // Recovers a marker from a Process run interrupted by a refresh/remount
  // before the backend committed PROCESSING, and clears the marker once the
  // run genuinely resolves.
  //
  // On a full-page reload, `isExecutionPending` starts `false` for one render —
  // if a separate "resolved" effect read that first render's stale value,
  // it would clear the just-found marker before the recovery's
  // `setExecutionPending(true)` had a chance to be observed. Checking the
  // marker first and returning immediately after recovering it defers the
  // resolved-check to the next render, once isExecutionPending/recoveredPending
  // actually reflect the recovery.
  //
  // Deliberately NOT keyed on `latestStatus` for the "resolved" check: that
  // flips to PROCESSING optimistically the instant `runProcess` fires, which
  // would clear the marker within milliseconds of writing it — before a
  // refresh or navigation had any chance to need it. `isExecutionPending`
  // only goes false via a real server poll or the mutation failing.
  const [recoveredPending, setRecoveredPending] = useState(false);
  useEffect(() => {
    if (!cohortId) return;

    if (!recoveredPending) {
      const timestamp = readCohortProcessPendingTimestamp(cohortId);
      if (timestamp !== null) {
        if (Date.now() - timestamp >= COHORT_PROCESS_PENDING_TTL_MS) {
          clearCohortProcessPendingMarker(cohortId);
        } else {
          setRecoveredPending(true);
          useCohortDefinitionStore.getState().setExecutionPending(true);
          return;
        }
      }
    }

    if (!isExecutionPending) {
      clearCohortProcessPendingMarker(cohortId);
      setRecoveredPending(false);
      return;
    }

    // While a recovered run is still in the pre-commit window, time the
    // marker out rather than pinning the button in "Queuing…" forever.
    if (!recoveredPending) return;
    const timestamp = readCohortProcessPendingTimestamp(cohortId);
    const remaining = timestamp ? COHORT_PROCESS_PENDING_TTL_MS - (Date.now() - timestamp) : 0;
    const timer = setTimeout(() => {
      clearCohortProcessPendingMarker(cohortId);
      setRecoveredPending(false);
      useCohortDefinitionStore.getState().setExecutionPending(false);
    }, Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [cohortId, isExecutionPending, recoveredPending]);

  const runProcess = useCallback(() => {
    // Written before the mutation fires so a refresh mid-request can recover it.
    if (cohortId) writeCohortProcessPendingMarker(cohortId);

    useCohortDefinitionStore.getState().clearChangeStatuses();
    dispatch(clearItemChangeStatuses());

    if (cohortId) {
      dispatch(markConceptGroupSelectionsAsReported({ markAll: true }));
      useCohortDefinitionStore.getState().markConceptGroupSelectionsAsReported({ markAll: true });
      emitSyncConceptGroupSelections({ cohortId, markAll: true });
    }

    void submitVersion();

    if (projectId && cohortId && latestCohortVersionId) {
      emitUserSaveEvent({
        model: researchAssistantModel,
        cohortVersionId: latestCohortVersionId,
        cohortId,
        projectId,
        user,
        cohortAction: COHORT_ACTIONS.PROCESS,
      });
    }
    dispatch(
      addResearchAssistantMessages([
        {
          message: intl.formatMessage({
            id: 'projects.cohortBuilderPage.researchAssistant.processedInfoMessage',
          }),
          timestamp: new Date().toISOString(),
          currentUser: 'user',
          isCurrentUser: true,
          type: ResearchAssistantMessageEnumLegacy.PROCESS,
        },
      ])
    );
  }, [
    dispatch,
    cohortId,
    submitVersion,
    projectId,
    latestCohortVersionId,
    researchAssistantModel,
    user,
    intl,
  ]);

  return {
    runProcess,
    // Includes recoveredPending so the button stays "Queuing…" across a refresh.
    isProcessSubmitting: isProcessSubmitting || recoveredPending,
    processError: processError ?? null,
    // False while loading — submitVersion no-ops silently until this clears.
    isProcessReady: !isInitialLoading,
  };
};
